import { useCallback, useEffect, useState } from "react";
import { Check, ChevronsUpDown, Stethoscope } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { loadManagedSpecialtyNames, SPECIALTIES } from "@/lib/specialties";

export default function SpecialtySelect({ value, onChange, className }) {
  const [open, setOpen] = useState(false);
  const [specialties, setSpecialties] = useState(SPECIALTIES);
  const [loading, setLoading] = useState(false);

  const refreshSpecialties = useCallback(async () => {
    setLoading(true);
    try {
      const names = await loadManagedSpecialtyNames();
      const next = value && !names.some((name) => name.toLowerCase() === value.toLowerCase())
        ? [value, ...names]
        : names;
      setSpecialties(next);
    } finally {
      setLoading(false);
    }
  }, [value]);

  useEffect(() => {
    refreshSpecialties();
  }, [refreshSpecialties]);

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen) refreshSpecialties();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("h-12 w-full justify-between rounded-full font-normal", !value && "text-muted-foreground", className)}
        >
          <span className="flex items-center gap-2 truncate">
            <Stethoscope size={16} className="shrink-0 text-muted-foreground" />
            {value || "Select specialty"}
          </span>
          <ChevronsUpDown size={16} className="shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput placeholder="Search specialties..." />
          <CommandList>
            <CommandEmpty>{loading ? "Loading specialties..." : "No specialty found."}</CommandEmpty>
            <CommandGroup>
              {specialties.map((s) => (
                <CommandItem
                  key={s}
                  value={s}
                  onSelect={() => {
                    onChange(s === value ? "" : s);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", value === s ? "opacity-100" : "opacity-0")}
                  />
                  {s}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
